/* =========================================================================
   Work Order — Detail (v2 redesign)
   Ported from docs/featureBriefs/design_handoff_work_orders_change_orders/prototype/app/work-orders.js
   (PAGES["wo-detail"], lines ~293-383, and the shared modals, lines ~559-612).
   ========================================================================= */
import React, { useCallback, useEffect, useState } from "react";
import "./WorkOrders.css";
import {
    AlertTriangle,
    Ban,
    Camera,
    Clock,
    CheckCircle2,
    DollarSign,
    Eye,
    FileText,
    HardHat,
    Hash,
    Image as ImageIcon,
    Mail,
    ClipboardList,
    MoreHorizontal,
    Package,
    RefreshCw,
    Send,
    UploadCloud,
    XCircle,
} from "lucide-react";
import { useSiteContext } from "../../../SiteContext";
import { can } from "../../../util/adminPermissions";
import { Page } from "../../../interfaces/PagesInterface";
import WorkOrdersManager from "../../../managers/WorkOrdersManager";
import ChangeOrdersManager from "../../../managers/ChangeOrdersManager";
import FileDropzone from "../../../components/FileDropzone";
import { openWorkOrderPdf, getWorkOrderEmailDefaults, openWorkOrderPdfVersion, openCustomerSignedDoc, getWorkOrderPhotoObjectUrl } from "../../../api/workOrders";
import {
    WorkOrderDetail,
    WorkOrderStatus,
    WorkOrderStatusHistory,
    WorkOrderItemPhoto,
} from "../../../interfaces/WorkOrderInterface";
import {
    WO_STATUS_META,
    money,
    fmtDate,
    fmtDateTime,
    woNumText,
    StatusPill,
    PageHead,
    SectionLabel,
    BackBar,
    EmptyState,
    Kv,
    Banner,
    DisabledNote,
    Timeline,
    TimelineEntry,
    DocRow,
    OverflowMenu,
    ModalShell,
    useToast,
} from "./woShared";

/* ---------------------------------------------------------------- helpers */

/** How the signature was captured, as written to WorkOrder.CustomerSignatureMethod
 *  by the three capture paths: at WO creation, deferred in the field, and via the
 *  emailed signing link. */
const SIGNATURE_METHOD_LABELS: Record<string, string> = {
    in_field: "Signed in field",
    in_field_later: "Signed in field (deferred)",
    digital_link: "Signed via emailed link",
};

/** Ported from the legacy WorkOrderDetailPage — derives a human title for a
 *  status-history row, disambiguating same-status rows (email sent / signature
 *  request sent) by their notes prefix. */
function getHistoryEntryTitle(entry: WorkOrderStatusHistory): string {
    if (entry.fromStatus == null) return "Work Order Created";
    if (entry.fromStatus === entry.toStatus) {
        if (entry.notes?.startsWith("Emailed PDF")) return "Emailed to Client";
        if (entry.notes?.startsWith("Sent signature request")) return "Signature Request Sent";
        return "Updated";
    }
    // The audit record of a signature correction. Checked before the generic
    // transition fallbacks so it does not read as a plain status change — the
    // admin's reason rides along in `notes`.
    if (entry.notes?.startsWith("Customer signature removed")) return "Customer Signature Removed";
    if (entry.fromStatus === WorkOrderStatus.PendingFieldSignature && entry.toStatus === WorkOrderStatus.PendingPO) {
        return "Customer Signed";
    }
    if (entry.toStatus === WorkOrderStatus.Voided) return "Voided";
    if (entry.toStatus === WorkOrderStatus.NoCharge) return "Marked No Charge";
    if (entry.toStatus === WorkOrderStatus.ReceivedPO) return "PO# Received";
    if (entry.toStatus === WorkOrderStatus.Invoiced) return "Invoiced";
    const fromName = WO_STATUS_META[entry.fromStatus]?.name ?? "Unknown";
    const toName = WO_STATUS_META[entry.toStatus]?.name ?? "Unknown";
    return `${fromName} → ${toName}`;
}

/** Ported from the legacy page — always show a "Work Order Created" entry even
 *  if the DB row is missing (older WOs may predate the initial-status-history
 *  insert in createWorkOrder). */
function buildDisplayHistory(wo: WorkOrderDetail): WorkOrderStatusHistory[] {
    const hasCreatedEntry = wo.statusHistory.some((e) => e.fromStatus == null);
    if (hasCreatedEntry) return wo.statusHistory;
    return [
        ...wo.statusHistory,
        {
            workOrderStatusHistoryID: -1,
            workOrderID: wo.workOrderID,
            fromStatus: null,
            toStatus: WorkOrderStatus.PendingFieldSignature,
            changedByAdminEmployeeID: null,
            changedByEmployeeID: wo.employeeID,
            notes: null,
            createdAt: wo.createdAt,
            createdAtOffset: wo.createdAtOffset,
            changedByName: wo.employeeName,
        },
    ];
}

function ModalError({ children }: { children: React.ReactNode }) {
    return <div style={{ color: "var(--danger)", fontSize: 12.5, fontWeight: 600 }}>{children}</div>;
}

const UPLOAD_ACCEPT = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
};

type ModalKind = "po" | "discount" | "invoice" | "void" | "noCharge" | "upload" | "email" | "sendSig" | "billingOnly" | "removeSig" | null;
type UndoKind = "void" | "noCharge";

/* ---------------------------------------------------------------- component */

const WorkOrderDetailPage: React.FC = () => {
    const { setPage, userRole } = useSiteContext();
    const canManageWorkOrders = can(userRole, "manageWorkOrders");
    const { toastNode, showToast } = useToast();

    const [workOrder, setWorkOrder] = useState<WorkOrderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [modal, setModal] = useState<ModalKind>(null);
    const [submitting, setSubmitting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const [poNumber, setPoNumber] = useState("");
    const [discountInput, setDiscountInput] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [voidReason, setVoidReason] = useState("");
    const [noChargeReason, setNoChargeReason] = useState("");
    const [removeSigReason, setRemoveSigReason] = useState("");
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadDragActive, setUploadDragActive] = useState(false);
    const [uploadNotes, setUploadNotes] = useState("");
    const [emailTo, setEmailTo] = useState("");
    const [emailCc, setEmailCc] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [emailDefaultsLoading, setEmailDefaultsLoading] = useState(false);
    const [sigEmail, setSigEmail] = useState("");

    const [undoing, setUndoing] = useState<UndoKind | null>(null);
    const [photoModal, setPhotoModal] = useState<WorkOrderItemPhoto | null>(null);
    // photoID → object URL. Photos live in the private blob container, so each
    // is streamed through the authenticated API and shown via an object URL.
    const [photoUrls, setPhotoUrls] = useState<Record<number, string>>({});

    const fetchWorkOrder = useCallback(async () => {
        const workOrderID = WorkOrdersManager.selectedWorkOrderID;
        if (!workOrderID) return;
        setLoading(true);
        setError(null);
        try {
            const data = await WorkOrdersManager.getWorkOrder(workOrderID);
            setWorkOrder(data);
        } catch {
            setError("Failed to load work order. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWorkOrder();
    }, [fetchWorkOrder]);

    // Stream each line-item photo through the API once the detail loads.
    useEffect(() => {
        if (!workOrder) return;
        const photos = workOrder.items.flatMap((it) => it.photos);
        if (!photos.length) return;
        let cancelled = false;
        const urls: string[] = [];
        (async () => {
            const entries = await Promise.all(
                photos.map(async (p) => {
                    try {
                        const url = await getWorkOrderPhotoObjectUrl(workOrder.workOrderID, p.workOrderItemPhotoID);
                        urls.push(url);
                        return [p.workOrderItemPhotoID, url] as const;
                    } catch {
                        return null;
                    }
                })
            );
            if (cancelled) return;
            setPhotoUrls(Object.fromEntries(entries.filter((e): e is readonly [number, string] => e !== null)));
        })();
        return () => {
            cancelled = true;
            urls.forEach((u) => window.URL.revokeObjectURL(u));
            setPhotoUrls({});
        };
    }, [workOrder]);

    const openModal = (kind: Exclude<ModalKind, null>) => {
        if (!workOrder) return;
        setModalError(null);
        setSubmitting(false);
        if (kind === "po") setPoNumber("");
        if (kind === "discount") setDiscountInput(workOrder.discountAmount ? workOrder.discountAmount.toFixed(2) : "");
        if (kind === "invoice") setInvoiceNumber("");
        if (kind === "void") setVoidReason("");
        if (kind === "noCharge") setNoChargeReason("");
        if (kind === "removeSig") setRemoveSigReason("");
        if (kind === "upload") {
            setUploadFile(null);
            setUploadDragActive(false);
            setUploadNotes("");
        }
        if (kind === "email") {
            // TAS-297/299: To pre-fills from the job contacts flagged for WO emails
            // (falling back to the WO's one-off customer email); CC defaults to the
            // sending admin so they own the thread. Both stay editable.
            setEmailTo(workOrder.customerEmail ?? "");
            setEmailCc("");
            setEmailBody(
                `Please find attached work order ${workOrder.altWorkOrderNumber || workOrder.workOrderID} for your review and signature.`
            );
            setEmailDefaultsLoading(true);
            getWorkOrderEmailDefaults(workOrder.workOrderID)
                .then((d) => {
                    if (d.to.length > 0) setEmailTo(d.to.join(", "));
                    if (d.adminEmail) setEmailCc(d.adminEmail);
                })
                .catch(() => { /* pre-fill only — the dialog still works untouched */ })
                .finally(() => setEmailDefaultsLoading(false));
        }
        if (kind === "sendSig") setSigEmail(workOrder.customerEmail ?? "");
        setModal(kind);
    };
    const closeModal = () => {
        if (submitting) return;
        setModal(null);
        setModalError(null);
    };

    const handleSavePO = async () => {
        if (!workOrder || !poNumber.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.setPONumber(workOrder.workOrderID, poNumber.trim());
            setModal(null);
            showToast("PO saved — status advanced to Received PO.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to save PO number. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveDiscount = async () => {
        if (!workOrder || submitting) return;
        const parsed = Number(discountInput) || 0;
        if (parsed < 0 || parsed > (workOrder.totalAmount ?? 0)) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.setDiscount(workOrder.workOrderID, parsed);
            setModal(null);
            showToast("Discount saved.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to save discount. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // TAS-442 — flip billing-only; the server refuses once hours have been claimed.
    const handleToggleBillingOnly = async () => {
        if (!workOrder || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            const next = !workOrder.billingOnly;
            // PUT /admin/workOrders/:id answers 200 { message } on success and 400 { message }
            // when the flag is locked (claims exist) — the 400 surfaces via the catch below.
            await WorkOrdersManager.setBillingOnly(workOrder.workOrderID, next);
            setModal(null);
            showToast(next ? "Marked billing only — time claims disabled." : "Time claims re-enabled.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to update billing-only. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveInvoice = async () => {
        if (!workOrder || !invoiceNumber.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.setInvoiceNumber(workOrder.workOrderID, invoiceNumber.trim());
            setModal(null);
            showToast("Invoice saved — status advanced to Invoiced.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to save invoice number. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleVoid = async () => {
        if (!workOrder || !voidReason.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.voidWorkOrder(workOrder.workOrderID, voidReason.trim());
            setModal(null);
            showToast("Work order voided.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to void work order. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleNoCharge = async () => {
        if (!workOrder || !noChargeReason.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.markNoCharge(workOrder.workOrderID, noChargeReason.trim());
            setModal(null);
            showToast("Marked as No Charge.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to mark as No Charge. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveSig = async () => {
        if (!workOrder || !removeSigReason.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.removeCustomerSignature(workOrder.workOrderID, removeSigReason.trim());
            setModal(null);
            showToast("Signature removed — work order reopened for signature.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to remove the signature. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleUploadDrop = (files: File[]) => {
        setUploadDragActive(false);
        if (files.length > 0) setUploadFile(files[0]);
    };

    const handleUpload = async () => {
        if (!workOrder || !uploadFile || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.uploadCustomerSignedDoc(workOrder.workOrderID, uploadFile, uploadNotes.trim() || undefined);
            setModal(null);
            showToast("Signed document uploaded.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to upload document. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEmail = async () => {
        if (!workOrder || !emailTo.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            const toList = emailTo.split(",").map((e) => e.trim()).filter(Boolean);
            const ccList = emailCc.split(",").map((e) => e.trim()).filter(Boolean);
            await WorkOrdersManager.emailWorkOrderPdf(workOrder.workOrderID, {
                recipient: toList[0],
                recipients: toList.slice(1),
                cc: ccList.length ? ccList : undefined,
                body: emailBody.trim() || undefined,
            });
            setModal(null);
            showToast("Work order emailed to client.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to send email. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSendSig = async () => {
        if (!workOrder || !sigEmail.trim() || submitting) return;
        setSubmitting(true);
        setModalError(null);
        try {
            await WorkOrdersManager.sendSignatureRequest(workOrder.workOrderID, { recipient: sigEmail.trim() });
            setModal(null);
            showToast("Signing link sent.");
            await fetchWorkOrder();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to send signing link. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleUndo = async (kind: UndoKind) => {
        if (!workOrder || undoing) return;
        setUndoing(kind);
        try {
            if (kind === "void") await WorkOrdersManager.undoVoid(workOrder.workOrderID);
            else await WorkOrdersManager.undoNoCharge(workOrder.workOrderID);
            showToast(kind === "void" ? "Void undone — previous status restored." : "No-charge undone — previous status restored.");
            await fetchWorkOrder();
        } catch {
            showToast(kind === "void" ? "Failed to undo void. Please try again." : "Failed to undo no charge. Please try again.");
        } finally {
            setUndoing(null);
        }
    };

    const handleViewPdf = () => {
        if (workOrder) openWorkOrderPdf(workOrder.workOrderID);
    };
    const handleViewPdfVersion = (versionNumber: number) => {
        if (workOrder) openWorkOrderPdfVersion(workOrder.workOrderID, versionNumber);
    };
    const handleViewSignedDoc = (versionNumber: number) => {
        if (workOrder) openCustomerSignedDoc(workOrder.workOrderID, versionNumber);
    };

    const openChangeOrder = () => {
        if (!workOrder) return;
        ChangeOrdersManager.selectedChangeOrderID = workOrder.changeOrderID ?? 0;
        setPage(Page.ChangeOrderDetail);
    };

    /* ---------------------------------------------------------- loading / error */

    if (loading && !workOrder) {
        return (
            <div className="wov2">
                <EmptyState Icon={Clock} title="Loading work order…" />
            </div>
        );
    }

    if (error && !workOrder) {
        return (
            <div className="wov2">
                <Banner
                    variant="danger"
                    Icon={AlertTriangle}
                    action={
                        <button className="btn sm" onClick={fetchWorkOrder}>
                            <RefreshCw size={14} />Retry
                        </button>
                    }
                >
                    {error}
                </Banner>
            </div>
        );
    }

    if (!workOrder) return null;

    /* ---------------------------------------------------------- derived */

    const meta = WO_STATUS_META[workOrder.status];
    const noPdf = !workOrder.pdfBlobUrl;
    const sortedItems = [...workOrder.items].sort((a, b) => a.itemNumber - b.itemNumber);
    const itemsTotalHours = sortedItems.reduce((s, it) => s + it.hours, 0);
    const hourClaims = workOrder.hourClaims ?? [];
    const claimsTotalHours = hourClaims.reduce((s, c) => s + (c.hoursClaimed ?? 0), 0);
    const claimsTotalAmount = hourClaims.reduce((s, c) => s + (c.amount ?? 0), 0);
    const itemsTotalAmount = sortedItems.reduce((s, it) => s + it.totalAmount, 0);
    const sub = `${workOrder.firstItemDescription ?? ""}${
        workOrder.itemCount > 1 ? ` · +${workOrder.itemCount - 1} more line item${workOrder.itemCount > 2 ? "s" : ""}` : ""
    }`;
    const displayHistory = buildDisplayHistory(workOrder);
    const timelineEntries: TimelineEntry[] = displayHistory.map((h) => {
        const hMeta = WO_STATUS_META[h.toStatus];
        return {
            key: h.workOrderStatusHistoryID,
            stClass: hMeta?.stClass ?? "st1",
            Icon: hMeta?.Icon ?? Clock,
            title: getHistoryEntryTitle(h),
            note: h.notes,
            when: fmtDateTime(h.createdAt),
            by: h.changedByName,
        };
    });
    const latestPdfVersion = workOrder.pdfVersions[0];
    // Either capture path counts as signed: a drawn/typed signature on the WO, or
    // a scanned signed copy the office uploaded. Mirrors the server's own check.
    const hasCustomerSignature = !!workOrder.customerSignature || !!workOrder.customerSignedDocumentBlobUrl;
    // Past billing — the server refuses a signature removal here and says to undo
    // the invoice/void first, so the modal warns instead of letting it 400.
    const signatureRemovalBlocked =
        workOrder.status === WorkOrderStatus.Invoiced || workOrder.status === WorkOrderStatus.Voided;

    const overflowItems = (voidOnly: boolean) =>
        voidOnly
            ? [{ label: "Void work order", Icon: XCircle, danger: true, onClick: () => openModal("void") }]
            : [
                  { label: "Mark as No Charge", Icon: Ban, danger: true, onClick: () => openModal("noCharge") },
                  "sep" as const,
                  { label: "Void work order", Icon: XCircle, danger: true, onClick: () => openModal("void") },
              ];

    return (
        <div className="wov2">
            <BackBar
                label="Work Orders"
                onBack={() => setPage(Page.WorkOrders)}
                crumb={
                    <>
                        <b>{meta?.name}</b> · {workOrder.projectName}
                    </>
                }
            />

            <PageHead Icon={ClipboardList} title={woNumText(workOrder)} sub={sub} />

            {workOrder.status === WorkOrderStatus.Voided && (
                <Banner
                    variant="danger"
                    Icon={XCircle}
                    action={
                        canManageWorkOrders ? (
                            <button className="btn sm" onClick={() => handleUndo("void")} disabled={undoing === "void"}>
                                <RefreshCw size={14} />{undoing === "void" ? "Undoing…" : "Undo void"}
                            </button>
                        ) : undefined
                    }
                >
                    <b>Voided {fmtDate(workOrder.voidedAt)}.</b> {workOrder.voidReason}
                </Banner>
            )}
            {workOrder.status === WorkOrderStatus.NoCharge && (
                <Banner
                    variant="neutral"
                    Icon={Ban}
                    action={
                        canManageWorkOrders ? (
                            <button className="btn sm" onClick={() => handleUndo("noCharge")} disabled={undoing === "noCharge"}>
                                <RefreshCw size={14} />{undoing === "noCharge" ? "Undoing…" : "Undo no charge"}
                            </button>
                        ) : undefined
                    }
                >
                    <b>No Charge {fmtDate(workOrder.noChargeAt)}.</b> {workOrder.noChargeReason}
                </Banner>
            )}

            {workOrder.status !== WorkOrderStatus.Voided && (
                <div className="actionrow">
                    {workOrder.status === WorkOrderStatus.PendingFieldSignature && canManageWorkOrders && (
                        <>
                            <button className="btn primary" onClick={() => openModal("upload")}>
                                <UploadCloud size={16} />Upload Signed Document
                            </button>
                            <button className="btn" disabled={noPdf} onClick={() => openModal("email")}>
                                <Mail size={16} />Email to Client
                            </button>
                            <button className="btn" disabled={noPdf} onClick={() => openModal("sendSig")}>
                                <Send size={16} />Send for Signature
                            </button>
                            <span className="grow" />
                            <OverflowMenu ButtonIcon={MoreHorizontal} items={overflowItems(false)} />
                        </>
                    )}
                    {workOrder.status === WorkOrderStatus.PendingPO && canManageWorkOrders && (
                        <>
                            <button className="btn primary" onClick={() => openModal("po")}>
                                <Hash size={16} />Add PO #
                            </button>
                            <button className="btn" disabled={noPdf} onClick={() => openModal("email")}>
                                <Mail size={16} />Email to Client
                            </button>
                            <span className="grow" />
                            <OverflowMenu ButtonIcon={MoreHorizontal} items={overflowItems(false)} />
                        </>
                    )}
                    {workOrder.status === WorkOrderStatus.ReceivedPO && canManageWorkOrders && (
                        <>
                            <button className="btn primary" onClick={() => openModal("invoice")}>
                                <DollarSign size={16} />Add Invoice #
                            </button>
                            <span className="grow" />
                            <OverflowMenu ButtonIcon={MoreHorizontal} items={overflowItems(false)} />
                        </>
                    )}
                    {workOrder.status === WorkOrderStatus.Invoiced && (
                        <>
                            <button className="btn" onClick={handleViewPdf}>
                                <FileText size={16} />View Current PDF
                            </button>
                            {canManageWorkOrders && (
                                <>
                                    <span className="grow" />
                                    <OverflowMenu ButtonIcon={MoreHorizontal} items={overflowItems(false)} />
                                </>
                            )}
                        </>
                    )}
                    {workOrder.status === WorkOrderStatus.NoCharge && (
                        <>
                            <button className="btn" onClick={handleViewPdf}>
                                <FileText size={16} />View Current PDF
                            </button>
                            {canManageWorkOrders && (
                                <>
                                    <span className="grow" />
                                    <OverflowMenu ButtonIcon={MoreHorizontal} items={overflowItems(true)} />
                                </>
                            )}
                        </>
                    )}
                    {!canManageWorkOrders &&
                        (workOrder.status === WorkOrderStatus.PendingFieldSignature ||
                            workOrder.status === WorkOrderStatus.PendingPO ||
                            workOrder.status === WorkOrderStatus.ReceivedPO) && (
                            <DisabledNote>Read-only — you don't have permission to manage work orders.</DisabledNote>
                        )}
                    {canManageWorkOrders && noPdf &&
                        (workOrder.status === WorkOrderStatus.PendingFieldSignature ||
                            workOrder.status === WorkOrderStatus.PendingPO) && (
                            <DisabledNote>Email & signature need a generated PDF — none yet on this WO.</DisabledNote>
                        )}
                </div>
            )}

            <div className="kv-grid">
                <Kv label="WO Number">
                    <span className="mono">{woNumText(workOrder)}</span>
                </Kv>
                <Kv label="Status">
                    <StatusPill kind="wo" status={workOrder.status} />
                </Kv>
                <Kv label="Total Amount">
                    <span className="mono">{money(workOrder.totalAmount)}</span>
                </Kv>
                <Kv label="Discount">
                    {workOrder.discountAmount > 0 ? (
                        <span className="mono">{money(workOrder.discountAmount)}</span>
                    ) : (
                        <span className="dash">–</span>
                    )}
                    {canManageWorkOrders && workOrder.status === WorkOrderStatus.PendingFieldSignature && (
                        <button className="linkbtn" style={{ marginLeft: 8 }} onClick={() => openModal("discount")}>
                            {workOrder.discountAmount > 0 ? "Edit" : "Add"}
                        </button>
                    )}
                </Kv>
                <Kv label="Amount Due">
                    <span className="mono" style={{ fontWeight: 700 }}>{money((workOrder.totalAmount ?? 0) - (workOrder.discountAmount ?? 0))}</span>
                </Kv>
                <Kv label="Total Hours">
                    <span className="mono">{(workOrder.totalHours ?? 0).toFixed(1)}</span>
                </Kv>
                <Kv label="Employee">{workOrder.employeeName}</Kv>
                <Kv label="Supervisor">{workOrder.wcdpSupervisorName || <span className="dash">–</span>}</Kv>
                <Kv label="Project">
                    {workOrder.projectName} <span className="muted mono">{workOrder.altProjectID}</span>
                </Kv>
                <Kv label="Contact">{workOrder.contactName}</Kv>
                <Kv label="City">{workOrder.city || <span className="dash">–</span>}</Kv>
                <Kv label="Department">
                    {workOrder.department}
                    {workOrder.departmentOther ? ` - ${workOrder.departmentOther}` : ""}
                </Kv>
                <Kv label="PO Number">
                    {workOrder.purchaseOrderNumber ? (
                        <span className="mono">{workOrder.purchaseOrderNumber}</span>
                    ) : (
                        <span className="dash">–</span>
                    )}
                </Kv>
                <Kv label="Invoice Number">
                    {workOrder.invoiceNumber ? (
                        <span className="mono">{workOrder.invoiceNumber}</span>
                    ) : (
                        <span className="dash">–</span>
                    )}
                </Kv>
                {workOrder.isInChangeOrder && (
                    <Kv label="Change Order">
                        <button className="linkbtn" onClick={openChangeOrder}>
                            COR #{workOrder.changeOrderCORNumber} →
                        </button>
                    </Kv>
                )}
                <Kv label="Created">{fmtDate(workOrder.createdAt)}</Kv>
                <Kv label="Time Claims">
                    {workOrder.billingOnly ? (
                        <span className="pill warn" title="Billing only — crew cannot claim hours">Billing only</span>
                    ) : (
                        <span className="pill ok">Enabled</span>
                    )}
                    {canManageWorkOrders && hourClaims.length === 0 && (
                        <button className="linkbtn" style={{ marginLeft: 8 }} onClick={() => { setModalError(null); setModal("billingOnly"); }}>
                            {workOrder.billingOnly ? "Enable claims" : "Mark billing only"}
                        </button>
                    )}
                </Kv>
            </div>

            <SectionLabel Icon={Package}>Line Items</SectionLabel>
            <div className="card">
                <div className="tbl-wrap">
                    <table className="dt">
                        <thead>
                            <tr>
                                <th className="cen">#</th>
                                <th>Description / Reason</th>
                                <th>Lots / Unit</th>
                                <th className="num">Hours</th>
                                <th className="num">Rate</th>
                                <th className="num">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map((item) => (
                                <React.Fragment key={item.workOrderItemID}>
                                    <tr>
                                        <td className="cen mono">{item.itemNumber}</td>
                                        <td className="li-desc">
                                            <span className="strong">{item.description}</span>
                                            {item.reasonForCorrection && <span className="rc">{item.reasonForCorrection}</span>}
                                        </td>
                                        <td>{item.lotsUnit}</td>
                                        <td className="num mono">{item.hours.toFixed(1)}</td>
                                        <td className="num mono">{money(item.hourlyRate)}</td>
                                        <td className="num mono strong">{money(item.totalAmount)}</td>
                                    </tr>
                                    {item.photos.length > 0 && (
                                        <tr className="li-photorow">
                                            <td></td>
                                            <td colSpan={5}>
                                                <div className="plabel">
                                                    <Camera size={13} />
                                                    {item.photos.length} field photo{item.photos.length > 1 ? "s" : ""}
                                                </div>
                                                <div className="photos">
                                                    {item.photos.map((p) => (
                                                        <div className="photo" key={p.workOrderItemPhotoID} onClick={() => setPhotoModal(p)}>
                                                            <div className="ph-img">
                                                                {photoUrls[p.workOrderItemPhotoID] ? (
                                                                    <img
                                                                        src={photoUrls[p.workOrderItemPhotoID]}
                                                                        alt={p.originalFileName ?? `Photo ${p.photoNumber}`}
                                                                    />
                                                                ) : (
                                                                    <ImageIcon />
                                                                )}
                                                            </div>
                                                            <div className="ph-cap">{p.originalFileName || `Photo ${p.photoNumber}`}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td></td>
                                <td className="strong">Total</td>
                                <td></td>
                                <td className="num mono strong">{itemsTotalHours.toFixed(1)}</td>
                                <td></td>
                                <td className="num mono strong">{money(itemsTotalAmount)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Employee hour claims (TAS-293) — who claimed, what they are, at what rate */}
            <SectionLabel Icon={HardHat}>Hour Claims</SectionLabel>
            <div className={`card${hourClaims.length === 0 ? " pad" : ""}`}>
                {hourClaims.length === 0 ? (
                    <div className="muted">
                        {workOrder.billingOnly
                            ? "Billing only — time claims are disabled for this work order."
                            : "No hours claimed against this work order yet."}
                    </div>
                ) : (
                    <div className="tbl-wrap">
                        <table className="dt">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Title</th>
                                    <th>Date</th>
                                    <th className="num">Hours</th>
                                    <th className="num">Rate</th>
                                    <th className="num">Amount</th>
                                    <th>Timesheet</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hourClaims.map((c) => (
                                    <tr key={c.employeeWorkOrderTaskID}>
                                        <td>
                                            <span className="strong">{c.employeeName}</span>
                                            {c.altEmployeeID && <span className="sub mono">#{c.altEmployeeID}</span>}
                                        </td>
                                        <td>{c.jobTitle ?? <span className="dash">–</span>}</td>
                                        <td className="mono">{fmtDate(c.entryDate)}</td>
                                        <td className="num mono">{c.hoursClaimed.toFixed(1)}</td>
                                        <td className="num mono">
                                            {money(c.hourlyRate)}
                                            {c.rateSource === "override" && (
                                                <span className="sub" title="Work order rate override set by a supervisor">override</span>
                                            )}
                                            {c.rateSource === "legacy" && (
                                                <span className="sub" title="Claimed before per-employee rates; flat task rate">flat rate</span>
                                            )}
                                        </td>
                                        <td className="num mono strong">{money(c.amount)}</td>
                                        <td>
                                            {c.submitted ? (
                                                <span className="pill ok"><CheckCircle2 /> Submitted</span>
                                            ) : (
                                                <span className="pill neutral">Open</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="strong">Total</td>
                                    <td></td>
                                    <td></td>
                                    <td className="num mono strong">{claimsTotalHours.toFixed(1)}</td>
                                    <td></td>
                                    <td className="num mono strong">{money(claimsTotalAmount)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>

            <div className="dash-two">
                <div>
                    <SectionLabel Icon={Clock}>Status History</SectionLabel>
                    <div className="card pad">
                        <Timeline entries={timelineEntries} />
                    </div>
                </div>
                <div>
                    <SectionLabel Icon={FileText}>Documents</SectionLabel>
                    <div className="card pad">
                        <DocRow
                            Icon={FileText}
                            name="Current work order PDF"
                            meta={
                                workOrder.pdfBlobUrl
                                    ? `Version ${latestPdfVersion?.versionNumber ?? 1} · ${fmtDate(latestPdfVersion?.createdAt ?? workOrder.createdAt)}`
                                    : "Not generated yet"
                            }
                            action={
                                workOrder.pdfBlobUrl ? (
                                    <button className="btn sm" onClick={handleViewPdf}>
                                        <Eye size={14} />View
                                    </button>
                                ) : (
                                    <span className="tag">
                                        <AlertTriangle size={12} />None
                                    </span>
                                )
                            }
                        />
                        {workOrder.pdfVersions.length > 0 && (
                            <>
                                <div className="subhead">PDF Versions</div>
                                {workOrder.pdfVersions.map((v) => (
                                    <DocRow
                                        key={v.workOrderPdfVersionID}
                                        Icon={FileText}
                                        name={`Version ${v.versionNumber}`}
                                        meta={`${fmtDate(v.createdAt)} · ${v.generatedReason}`}
                                        action={
                                            <button className="btn sm ghost" onClick={() => handleViewPdfVersion(v.versionNumber)}>
                                                <Eye size={14} />View
                                            </button>
                                        }
                                    />
                                ))}
                            </>
                        )}
                        {hasCustomerSignature && (
                            <>
                                <div className="subhead">Customer Signature</div>
                                <DocRow
                                    Icon={CheckCircle2}
                                    iconVariant="ok"
                                    name={workOrder.customerPrintedName || "Signed by customer"}
                                    meta={[
                                        workOrder.customerTitle,
                                        workOrder.customerSignatureDate ? fmtDate(workOrder.customerSignatureDate) : null,
                                        SIGNATURE_METHOD_LABELS[workOrder.customerSignatureMethod ?? ""] ?? workOrder.customerSignatureMethod,
                                    ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    action={
                                        canManageWorkOrders ? (
                                            <button className="btn sm ghost" onClick={() => openModal("removeSig")}>
                                                <XCircle size={14} />Remove
                                            </button>
                                        ) : undefined
                                    }
                                />
                            </>
                        )}
                        {workOrder.customerSignedDocVersions.length > 0 ? (
                            <>
                                <div className="subhead">Customer Signed Documents</div>
                                {workOrder.customerSignedDocVersions.map((s) => (
                                    <DocRow
                                        key={s.workOrderCustomerSignedDocVersionID}
                                        Icon={CheckCircle2}
                                        iconVariant="ok"
                                        name={`Signed copy v${s.versionNumber}`}
                                        meta={`${fmtDate(s.createdAt)} · ${s.notes ?? ""}`}
                                        action={
                                            <button className="btn sm ghost" onClick={() => handleViewSignedDoc(s.versionNumber)}>
                                                <Eye size={14} />View
                                            </button>
                                        }
                                    />
                                ))}
                            </>
                        ) : (
                            <EmptyState Icon={FileText}>No customer-signed documents yet.</EmptyState>
                        )}
                    </div>
                </div>
            </div>

            {/* ---------------------------------------------------------- modals */}

            {modal === "po" && (
                <ModalShell
                    Icon={Hash}
                    title="Add PO Number"
                    sub={`${woNumText(workOrder)} · advances to Received PO`}
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn primary" onClick={handleSavePO} disabled={submitting || !poNumber.trim()}>
                                {submitting ? "Saving…" : "Save PO & advance"}
                            </button>
                        </>
                    }
                >
                    <Banner variant="info" Icon={RefreshCw}>
                        Entering a PO regenerates the work-order PDF and moves it from <b>Pending PO</b> to <b>Received PO</b>.
                    </Banner>
                    <div className="field">
                        <label>Purchase Order Number</label>
                        <input
                            className="input"
                            placeholder="e.g. PO-44120"
                            autoFocus
                            value={poNumber}
                            onChange={(e) => setPoNumber(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "discount" && (
                <ModalShell
                    Icon={DollarSign}
                    title="Customer Discount"
                    sub={woNumText(workOrder)}
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                onClick={handleSaveDiscount}
                                disabled={
                                    submitting ||
                                    (Number(discountInput) || 0) < 0 ||
                                    (Number(discountInput) || 0) > (workOrder.totalAmount ?? 0)
                                }
                            >
                                {submitting ? "Saving…" : "Save"}
                            </button>
                        </>
                    }
                >
                    <p className="muted">Discount negotiated with the customer. Only editable until the customer signs.</p>
                    <div className="field">
                        <label>Discount Amount</label>
                        <input
                            className="input"
                            type="number"
                            min={0}
                            step={0.01}
                            max={workOrder.totalAmount ?? 0}
                            autoFocus
                            value={discountInput}
                            onChange={(e) => setDiscountInput(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    <div className="kv-grid">
                        <Kv label="Subtotal">
                            <span className="mono">{money(workOrder.totalAmount)}</span>
                        </Kv>
                        <Kv label="Amount Due">
                            <span className="mono" style={{ fontWeight: 700 }}>
                                {money((workOrder.totalAmount ?? 0) - (Number(discountInput) || 0))}
                            </span>
                        </Kv>
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "billingOnly" && (
                <ModalShell
                    Icon={HardHat}
                    title={workOrder.billingOnly ? "Re-enable time claims" : "Mark as billing only"}
                    sub={woNumText(workOrder)}
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn primary" onClick={handleToggleBillingOnly} disabled={submitting}>
                                {submitting ? "Saving…" : workOrder.billingOnly ? "Enable claims" : "Mark billing only"}
                            </button>
                        </>
                    }
                >
                    <Banner variant="info" Icon={HardHat}>
                        {workOrder.billingOnly
                            ? "Crew will be able to claim hours against this work order again."
                            : "Crew will not be able to claim hours against this work order, and nothing from it will reach timesheets or incentive. The customer-facing side (signature, PO, invoice) is unchanged."}
                    </Banner>
                    <p className="muted">This can only be changed while no hours have been claimed.</p>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "invoice" && (
                <ModalShell
                    Icon={DollarSign}
                    title="Add Invoice Number"
                    sub={`${woNumText(workOrder)} · advances to Invoiced`}
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn primary" onClick={handleSaveInvoice} disabled={submitting || !invoiceNumber.trim()}>
                                {submitting ? "Saving…" : "Save invoice & advance"}
                            </button>
                        </>
                    }
                >
                    <Banner variant="info" Icon={RefreshCw}>
                        Entering an invoice number moves this work order from <b>Received PO</b> to <b>Invoiced</b>.
                    </Banner>
                    <div className="field">
                        <label>Invoice Number</label>
                        <input
                            className="input"
                            placeholder="e.g. INV-20551"
                            autoFocus
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "void" && (
                <ModalShell
                    Icon={XCircle}
                    danger
                    title="Void this work order?"
                    sub="Reversible — you can undo a void afterward."
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn danger" onClick={handleVoid} disabled={submitting || !voidReason.trim()}>
                                {submitting ? "Voiding…" : "Void work order"}
                            </button>
                        </>
                    }
                >
                    <div className="field">
                        <label>Reason for voiding *</label>
                        <textarea
                            className="input"
                            placeholder="e.g. Duplicate of another WO…"
                            autoFocus
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "noCharge" && (
                <ModalShell
                    Icon={Ban}
                    danger
                    title="Mark as No Charge?"
                    sub="Reversible — you can undo this afterward."
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn danger" onClick={handleNoCharge} disabled={submitting || !noChargeReason.trim()}>
                                {submitting ? "Saving…" : "Mark No Charge"}
                            </button>
                        </>
                    }
                >
                    <div className="field">
                        <label>Reason *</label>
                        <textarea
                            className="input"
                            placeholder="e.g. Warranty goodwill…"
                            autoFocus
                            value={noChargeReason}
                            onChange={(e) => setNoChargeReason(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "removeSig" && (
                <ModalShell
                    Icon={XCircle}
                    danger
                    title="Remove this customer signature?"
                    sub="The work order goes back to Pending Field Signature and its PDF is reissued unsigned."
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button
                                className="btn danger"
                                onClick={handleRemoveSig}
                                disabled={submitting || signatureRemovalBlocked || !removeSigReason.trim()}
                            >
                                {submitting ? "Removing…" : "Remove signature"}
                            </button>
                        </>
                    }
                >
                    {signatureRemovalBlocked ? (
                        <Banner variant="warn" Icon={AlertTriangle}>
                            {workOrder.status === WorkOrderStatus.Invoiced
                                ? "This work order has already been invoiced. Clear the invoice number before removing the signature."
                                : "This work order is voided. Undo the void before removing the signature."}
                        </Banner>
                    ) : (
                        <>
                            <div className="field">
                                <label>Reason for removing *</label>
                                <textarea
                                    className="input"
                                    placeholder="e.g. Signed by the wrong person…"
                                    autoFocus
                                    value={removeSigReason}
                                    onChange={(e) => setRemoveSigReason(e.target.value)}
                                    disabled={submitting}
                                />
                            </div>
                            <DisabledNote>
                                The reason, your name and the time are recorded in this work order's status
                                history. Previously signed copies and PDF versions stay archived under
                                Documents.
                            </DisabledNote>
                        </>
                    )}
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "upload" && (
                <ModalShell
                    Icon={UploadCloud}
                    title="Upload Signed Document"
                    sub="Attach a scanned wet-ink copy"
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn primary" onClick={handleUpload} disabled={submitting || !uploadFile}>
                                {submitting ? "Uploading…" : "Upload & mark signed"}
                            </button>
                        </>
                    }
                >
                    <FileDropzone
                        onDrop={handleUploadDrop}
                        selectedFile={uploadFile}
                        isDragActive={uploadDragActive}
                        accept={UPLOAD_ACCEPT}
                        placeholderText="Drag a PDF or image here, or click to browse"
                    />
                    <div className="field">
                        <label>Notes (optional)</label>
                        <textarea
                            className="input"
                            placeholder="Optional notes (e.g. who signed, when received)…"
                            value={uploadNotes}
                            onChange={(e) => setUploadNotes(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "email" && (
                <ModalShell
                    Icon={Mail}
                    title="Email to Client"
                    sub="Send the current work-order PDF"
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn primary" onClick={handleEmail} disabled={submitting || !emailTo.trim()}>
                                {submitting ? "Sending…" : "Send email"}
                            </button>
                        </>
                    }
                >
                    <div className="field">
                        <label>To</label>
                        <input
                            className="input"
                            placeholder={emailDefaultsLoading ? "Loading configured recipients…" : "client@example.com, second@example.com"}
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            disabled={submitting}
                        />
                        <span className="sub" style={{ marginTop: 4 }}>
                            Pre-filled from the job's contacts marked "Receives work order emails". Comma-separate multiple addresses.
                        </span>
                    </div>
                    <div className="field">
                        <label>CC</label>
                        <input
                            className="input"
                            placeholder="you@wcdp.com"
                            value={emailCc}
                            onChange={(e) => setEmailCc(e.target.value)}
                            disabled={submitting}
                        />
                        <span className="sub" style={{ marginTop: 4 }}>
                            Defaults to you so you have the thread; replies also come to you. Clear it to opt out.
                        </span>
                    </div>
                    <div className="field">
                        <label>Message</label>
                        <textarea
                            className="input"
                            placeholder="Optional note to the builder…"
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {modal === "sendSig" && (
                <ModalShell
                    Icon={Send}
                    title="Send for Signature"
                    sub="Email a secure signing link"
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </button>
                            <button className="btn primary" onClick={handleSendSig} disabled={submitting || !sigEmail.trim()}>
                                {submitting ? "Sending…" : "Send signing link"}
                            </button>
                        </>
                    }
                >
                    <Banner variant="info" Icon={Send}>
                        The client gets a tokenized link to sign on any device. Status updates automatically once signed.
                    </Banner>
                    <div className="field">
                        <label>Signer email</label>
                        <input
                            className="input"
                            placeholder="client@example.com"
                            value={sigEmail}
                            onChange={(e) => setSigEmail(e.target.value)}
                            disabled={submitting}
                        />
                    </div>
                    {modalError && <ModalError>{modalError}</ModalError>}
                </ModalShell>
            )}

            {photoModal && (
                <ModalShell
                    Icon={Camera}
                    title="Field Photo"
                    sub={photoModal.originalFileName || `Photo ${photoModal.photoNumber}`}
                    onClose={() => setPhotoModal(null)}
                >
                    {photoUrls[photoModal.workOrderItemPhotoID] ? (
                        <img
                            src={photoUrls[photoModal.workOrderItemPhotoID]}
                            alt={photoModal.originalFileName ?? `Photo ${photoModal.photoNumber}`}
                            style={{ display: "block", margin: "0 auto", maxHeight: "60vh", maxWidth: "100%", borderRadius: 12 }}
                        />
                    ) : (
                        <div
                            style={{
                                height: 300, borderRadius: 12, background: "var(--surface-3)",
                                display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)",
                            }}
                        >
                            <ImageIcon size={54} style={{ opacity: 0.6 }} />
                        </div>
                    )}
                </ModalShell>
            )}

            {toastNode}
        </div>
    );
};

export default WorkOrderDetailPage;
